export const startOfMatchMark = '\u0001';
export const endOfMatchMark = '\u0002';
export const startOfHighlightMark = '\u0005';
export const endOfHighlightMark = '\u0006';

export const startOrEndMarksRe = new RegExp(
  `[${startOfMatchMark}${endOfMatchMark}${startOfHighlightMark}${endOfHighlightMark}]`,
  'g',
);

export const highlightMarkGroupsRe = new RegExp(
  `([^${startOfHighlightMark}]*)(?:${startOfHighlightMark}([^${endOfHighlightMark}]*)${endOfHighlightMark})?`,
  'g',
);
export const matchMarkGroupsRe = new RegExp(
  `([^${startOfMatchMark}]*)(?:${startOfMatchMark}([^${endOfMatchMark}]*)${endOfMatchMark})?`,
  'g',
);
export const collapseSiblingRe = new RegExp(`${endOfMatchMark}${startOfMatchMark}`, 'g');

export function joinMatchedAndHighlightedMarks(matched: string, highlighted: string) {
  let i = 0;
  let j = 0;
  let inHighlighted = false;
  const result: string[] = [];

  while (i < matched.length && j < highlighted.length) {
    if (matched[i] === startOfMatchMark && matched[i + 1] === endOfMatchMark) {
      i += 2;
      continue;
    }

    if (matched[i] === endOfMatchMark && matched[i + 1] === startOfMatchMark) {
      i += 2;
      continue;
    }

    if (highlighted[j] === startOfHighlightMark && highlighted[j + 1] === endOfHighlightMark) {
      j += 2;
      continue;
    }

    if (highlighted[j] === endOfHighlightMark && highlighted[j + 1] === startOfHighlightMark) {
      j += 2;
      continue;
    }

    if (matched[i] === startOfMatchMark) {
      if (inHighlighted) {
        result.push(endOfHighlightMark, startOfMatchMark, startOfHighlightMark);
      } else {
        result.push(startOfMatchMark);
      }

      ++i;
      continue;
    }

    if (highlighted[j] === startOfHighlightMark) {
      inHighlighted = true;
      result.push(startOfHighlightMark);
      ++j;
      continue;
    }

    if (highlighted[j] === endOfHighlightMark) {
      inHighlighted = false;
      result.push(endOfHighlightMark);
      ++j;
      continue;
    }

    if (matched[i] === endOfMatchMark) {
      if (inHighlighted) {
        result.push(endOfHighlightMark, endOfMatchMark, startOfHighlightMark);
      } else {
        result.push(endOfMatchMark);
      }

      ++i;
      continue;
    }

    if (matched[i] !== highlighted[j]) {
      // at this moment there should be no marks in both string, and misaligned char means both strings are mismatching
      return highlighted;
    }

    result.push(matched[i]);
    ++i;
    ++j;
  }

  while (i < matched.length) {
    result.push(matched[i]);
    ++i;
  }

  while (j < highlighted.length) {
    result.push(highlighted[j]);
    ++j;
  }

  return result.join('');
}

type Token = {
  value: string;
  highlightMode: 'common' | 'mark' | 'separator';
  matchMode: 'span' | 'mark';
};

export function tokenizeTextWithMatchedAndHighlightedMarks(text: string | undefined): Token[] {
  if (!text) {
    return [];
  }

  const result: Token[] = [];
  let highlightMode: Token['highlightMode'] = 'common';
  let matchMode: Token['matchMode'] = 'span';
  let last = '';

  for (const char of text) {
    switch (char) {
      case '/':
      case '\\':
      case '.':
        if (last) {
          result.push({ value: last, highlightMode, matchMode });
          last = '';
        }

        result.push({ value: char, highlightMode: 'separator', matchMode });
        break;
      case startOfHighlightMark:
        if (last && highlightMode !== 'mark') {
          result.push({ value: last, highlightMode, matchMode });
          last = '';
        }

        highlightMode = 'mark';
        break;
      case endOfHighlightMark:
        if (last && highlightMode !== 'common') {
          result.push({ value: last, highlightMode, matchMode });
          last = '';
        }

        highlightMode = 'common';
        break;
      case startOfMatchMark:
        if (last) {
          result.push({ value: last, highlightMode, matchMode });
          last = '';
        }

        matchMode = 'mark';
        break;
      case endOfMatchMark:
        if (last) {
          result.push({ value: last, highlightMode, matchMode });
          last = '';
        }

        matchMode = 'span';
        break;
      default:
        last += char;
        break;
    }
  }

  if (last) {
    result.push({ value: last, highlightMode, matchMode });
  }

  return result;
}
